/**
 * Generic-CSV column mapping.
 *
 * The Slice 2 Generic CSV format is intentionally permissive: most
 * shops export their old DMS with a vendor template that names
 * "Repair Order #" / "RO Number" / "Invoice No." for the same field.
 * Rather than asking the customer to remap headers in the UI before
 * we know what we're missing, we accept a wide alias set and only
 * fall back to a manual remap dialog (Slice 2.4 UI work) when a
 * REQUIRED logical field has no matching header.
 *
 * Logical fields are the field set of `ParsedRepairOrder /
 * ParsedJob / ParsedCustomer / ParsedVehicle` — but Generic CSV is
 * flat (one row = one job line under one RO under one customer +
 * vehicle), so a single source row contributes to all four parsed
 * shapes simultaneously.
 *
 * ─── Canonical normalization map (Batch 9C documentation) ─────────
 *
 * The end-to-end identity for people-related fields:
 *
 *   CSV header           → LogicalField          → Parsed field
 *   "Technician Name"    → jobTechnicianName     → ParsedJob.technicianDisplayName
 *   "Advisor Name"       → roAdvisorName         → ParsedRepairOrder.advisorDisplayName
 *
 * From there:
 *
 *   ParsedJob.technicianDisplayName   → inferTechnicianRoster()
 *     → shop_setup_inferences.payload.technicians[].displayName
 *     → buildPage3 / buildWizardInitialWithRoster.suggestedTechnicianRoster
 *     → Step 3 row prefill + Add Team Member next-name suggestion.
 *
 *   ParsedRepairOrder.advisorDisplayName → persisted on
 *     shop_history_repair_orders.advisor_display_name
 *     → OnboardSetupImportWizard.fetchDistinctAdvisorNames()
 *     → buildPage3 advisorNames param
 *     → buildWizardInitialWithRoster.suggestedAdvisorRoster
 *     → Step 3 SERVICE_WRITER row prefill + Add Team Member next-name.
 *
 * If a future CSV variant uses a different header for either column,
 * adding the alias to `ALIASES.jobTechnicianName` / `roAdvisorName`
 * below is sufficient — every downstream consumer reads the
 * canonical LogicalField, not the source header.
 */

export type LogicalField =
  // RO
  | 'roNumber'
  | 'externalRepairOrderId'
  | 'openedAt'
  | 'closedAt'
  | 'promisedAt'
  | 'sourceStatus'
  | 'odometerIn'
  | 'odometerOut'
  | 'subtotalCents'
  | 'taxCents'
  | 'discountCents'
  | 'totalCents'
  // Customer
  | 'externalCustomerId'
  | 'customerFirstName'
  | 'customerLastName'
  | 'customerCompanyName'
  | 'customerEmail'
  | 'customerPhone'
  | 'customerAddressLine1'
  | 'customerAddressLine2'
  | 'customerCity'
  | 'customerRegion'
  | 'customerPostalCode'
  | 'customerCountry'
  // Vehicle
  | 'externalVehicleId'
  | 'vehicleVin'
  | 'vehiclePlate'
  | 'vehiclePlateState'
  | 'vehicleYear'
  | 'vehicleMake'
  | 'vehicleModel'
  | 'vehicleTrim'
  | 'vehicleEngine'
  | 'vehicleColor'
  | 'vehicleOdometerLast'
  // Job
  | 'externalLineId'
  | 'jobLineNumber'
  | 'jobDescription'
  | 'jobOpCode'
  | 'jobLaborHours'
  | 'jobLaborCents'
  | 'jobPartsCents'
  | 'jobTaxCents'
  | 'jobDiscountCents'
  | 'jobTechnicianId'
  | 'jobTechnicianName'
  | 'jobIsWarranty'
  | 'jobIsComeback'
  // Header-level technician/advisor
  | 'roAdvisorName'
  | 'roAdvisorId'
  | 'roTechnicianId';

/**
 * Aliases checked LEFT-to-RIGHT. The matcher is case- and
 * punctuation-insensitive (we strip everything that isn't a-z0-9
 * before comparing), so add new entries verbatim — no need to
 * include casing variants.
 */
const ALIASES: Record<LogicalField, string[]> = {
  // RO
  roNumber: ['ro', 'ro number', 'ro #', 'repair order', 'repair order number', 'invoice', 'invoice number', 'invoice #', 'work order', 'wo', 'wo number'],
  externalRepairOrderId: ['ro id', 'repair order id', 'invoice id', 'work order id', 'document id', 'ticket id'],
  openedAt: ['opened', 'opened at', 'open date', 'date opened', 'created', 'created at', 'invoice date', 'service date', 'ro opened at', 'ro open date', 'ro opened', 'repair order opened at', 'repair order open date'],
  closedAt: ['closed', 'closed at', 'close date', 'date closed', 'completed', 'completed at', 'paid date', 'ro closed at', 'ro close date', 'ro closed', 'repair order closed at', 'repair order close date'],
  promisedAt: ['promised', 'promised at', 'promise date', 'due date', 'due', 'eta'],
  sourceStatus: ['status', 'ro status', 'invoice status', 'document status'],
  odometerIn: ['mileage in', 'odometer in', 'miles in', 'odo in'],
  odometerOut: ['mileage out', 'odometer out', 'miles out', 'odo out', 'mileage', 'odometer'],
  subtotalCents: ['subtotal', 'sub total', 'pretax total'],
  taxCents: ['tax', 'sales tax', 'total tax', 'tax amount', 'ro tax', 'ro tax amount'],
  discountCents: ['discount', 'discounts', 'total discount'],
  totalCents: ['total', 'grand total', 'invoice total', 'amount', 'ro total'],
  // Customer
  externalCustomerId: ['customer id', 'cust id', 'customer #', 'account', 'account number', 'account #', 'customer guid'],
  customerFirstName: ['customer first name', 'first name', 'firstname', 'cust first'],
  customerLastName: ['customer last name', 'last name', 'lastname', 'cust last'],
  customerCompanyName: ['company', 'company name', 'business name', 'organization'],
  customerEmail: ['email', 'customer email', 'e-mail'],
  customerPhone: ['phone', 'phone number', 'mobile', 'cell', 'home phone', 'customer phone'],
  customerAddressLine1: ['address', 'address 1', 'street', 'street address'],
  customerAddressLine2: ['address 2', 'unit', 'suite', 'apt'],
  customerCity: ['city', 'town'],
  customerRegion: ['state', 'region', 'province', 'state/province'],
  customerPostalCode: ['zip', 'zip code', 'postal code', 'postcode'],
  customerCountry: ['country'],
  // Vehicle
  externalVehicleId: ['vehicle id', 'veh id', 'vehicle #', 'vehicle guid'],
  vehicleVin: ['vin'],
  vehiclePlate: ['plate', 'license plate', 'tag'],
  vehiclePlateState: ['plate state', 'license state', 'tag state'],
  vehicleYear: ['year', 'vehicle year', 'model year'],
  vehicleMake: ['make', 'vehicle make'],
  vehicleModel: ['model', 'vehicle model'],
  vehicleTrim: ['trim', 'vehicle trim', 'submodel'],
  vehicleEngine: ['engine', 'vehicle engine', 'engine size', 'vehicle engine size'],
  vehicleColor: ['color', 'colour', 'vehicle color'],
  vehicleOdometerLast: ['last odometer', 'last mileage'],
  // Job
  externalLineId: ['line id', 'job id', 'op id', 'line guid'],
  jobLineNumber: ['line', 'line #', 'line number', 'seq', 'sequence', 'job line', 'job line number', 'job line #'],
  jobDescription: ['job', 'job description', 'description', 'service', 'service description', 'op description', 'work description'],
  jobOpCode: ['op code', 'opcode', 'op', 'service code', 'job code', 'sku'],
  jobLaborHours: ['hours', 'labor hours', 'billed hours', 'flat rate hours', 'labor hours sold', 'sold hours'],
  jobLaborCents: ['labor', 'labor amount', 'labor total', 'labor charge'],
  jobPartsCents: ['parts', 'parts amount', 'parts total', 'parts charge'],
  jobTaxCents: ['line tax', 'job tax'],
  jobDiscountCents: ['line discount', 'job discount'],
  jobTechnicianId: ['tech id', 'technician id', 'mechanic id'],
  jobTechnicianName: ['tech', 'technician', 'mechanic', 'tech name', 'technician name'],
  jobIsWarranty: ['warranty', 'is warranty'],
  jobIsComeback: ['comeback', 'is comeback', 'rework'],
  // RO advisor/tech
  roAdvisorName: ['advisor', 'service advisor', 'writer', 'service writer', 'advisor name'],
  roAdvisorId: ['advisor id', 'service advisor id', 'writer id', 'service writer id'],
  roTechnicianId: ['ro tech id', 'primary tech id'],
};

function normalizeHeader(header: string): string {
  return header
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')
    .trim();
}

const NORMALIZED_ALIASES: Record<LogicalField, string[]> = (() => {
  const out = {} as Record<LogicalField, string[]>;
  for (const key of Object.keys(ALIASES) as LogicalField[]) {
    out[key] = ALIASES[key].map(normalizeHeader);
  }
  return out;
})();

export type ColumnMap = Partial<Record<LogicalField, number>>;

/**
 * Build a column map from the header row. Each logical field maps
 * to the first matching column index (or is omitted entirely when
 * no alias matched).
 *
 * @returns map plus the list of unmapped headers (for the UI's
 *          "review your column mapping" preview).
 */
export function buildColumnMap(headers: string[]): {
  map: ColumnMap;
  unmappedHeaders: Array<{ index: number; header: string }>;
} {
  const map: ColumnMap = {};
  const normalizedHeaders = headers.map(normalizeHeader);
  const used = new Set<number>();

  for (const field of Object.keys(NORMALIZED_ALIASES) as LogicalField[]) {
    const aliases = NORMALIZED_ALIASES[field];
    for (let i = 0; i < normalizedHeaders.length; i++) {
      if (used.has(i)) continue;
      if (aliases.includes(normalizedHeaders[i])) {
        map[field] = i;
        used.add(i);
        break;
      }
    }
  }

  const unmappedHeaders = headers
    .map((header, index) => ({ index, header }))
    .filter(({ index }) => !used.has(index) && headers[index].trim().length > 0);

  return { map, unmappedHeaders };
}

/**
 * Logical fields without which the Generic CSV parser cannot
 * produce a usable repair order. The UI surfaces these as the
 * "must be mapped" set when a remap dialog is opened.
 */
export const REQUIRED_FIELDS: LogicalField[] = [
  'openedAt',
  'jobDescription',
];

/**
 * "Strongly recommended" set — the parser still works without them
 * but Slice 2 inference quality drops sharply (no advisor metrics,
 * no technician roster, no parts/labor split, etc.).
 */
export const RECOMMENDED_FIELDS: LogicalField[] = [
  'roNumber',
  'externalCustomerId',
  'externalVehicleId',
  'jobTechnicianName',
  'jobLaborCents',
  'jobPartsCents',
  'totalCents',
];
